import { useEffect, useMemo, useState } from 'react';
import { FiAlertCircle, FiInbox, FiUserX, FiUsers } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { taskService } from '../services/taskService';
import { workflowService } from '../services/workflowService';
import { Stage, Task, Workflow } from '../types';
import Avatar from '../components/Avatar';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import { isCompletedStatus } from '../utils/status';
import { isEnquiryIntake } from '../utils/stageFormRegistry';

interface StageQueue {
  stage: Stage;
  label: string;
  tasks: Task[];
}

type SliceKey = 'done' | 'rework' | 'overdue' | 'working' | 'open';

const SLICES: { key: SliceKey; label: string; color: string }[] = [
  { key: 'done', label: 'Done', color: '#067647' },
  { key: 'working', label: 'Working', color: '#1D4ED8' },
  { key: 'overdue', label: 'Overdue', color: '#C9372C' },
  { key: 'rework', label: 'Came back', color: '#B54708' },
  { key: 'open', label: 'Open', color: '#434E78' },
];

const isWorking = (task: Task) => {
  const status = task.status.toLowerCase();
  return status.includes('progress') || status.includes('active');
};

/** One enquiry, one slice, so the pie adds up to the stage total. */
const bucketOf = (task: Task): SliceKey => {
  if (isCompletedStatus(task.status)) return 'done';
  if (task.needsRework) return 'rework';
  if (task.isOverdue) return 'overdue';
  if (isWorking(task)) return 'working';
  return 'open';
};

const slicesOf = (tasks: Task[]) => {
  const counts: Record<SliceKey, number> = { done: 0, rework: 0, overdue: 0, working: 0, open: 0 };
  tasks.forEach((task) => {
    counts[bucketOf(task)] += 1;
  });
  return SLICES.map((slice) => ({ ...slice, value: counts[slice.key] }));
};

const ring = (cx: number, cy: number, outer: number, inner: number, start: number, end: number) => {
  const large = end - start > Math.PI ? 1 : 0;
  const at = (radius: number, angle: number) => [cx + radius * Math.cos(angle), cy + radius * Math.sin(angle)];
  const [x1, y1] = at(outer, start);
  const [x2, y2] = at(outer, end);
  const [x3, y3] = at(inner, end);
  const [x4, y4] = at(inner, start);
  return `M ${x1} ${y1} A ${outer} ${outer} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${inner} ${inner} 0 ${large} 0 ${x4} ${y4} Z`;
};

const PieChart = ({ slices, title, chartId }: { slices: { label: string; value: number; color: string }[]; title: string; chartId: string }) => {
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const summary = slices.map((slice) => `${slice.label} ${slice.value}`).join(', ');
  const cx = 80;
  const cy = 80;
  const outer = 68;
  const inner = 44;
  const gap = 0.06;
  let angle = -Math.PI / 2;
  const parts = total === 0
    ? []
    : slices.filter((slice) => slice.value > 0).map((slice) => {
        const sweep = (slice.value / total) * Math.PI * 2;
        const start = angle;
        angle += sweep;
        const pad = sweep > gap * 3 ? gap : 0;
        return { ...slice, start: start + pad / 2, end: angle - pad / 2 };
      });
  const filterId = `donut-${chartId}`;

  return (
    <figure className="flex flex-wrap items-center gap-6">
      <svg viewBox="0 0 160 160" className="w-44 h-44 shrink-0" role="img" aria-label={`${title}. ${summary || 'No enquiries'}`}>
        <title>{title}</title>
        <defs>
          <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#11152A" floodOpacity="0.14" />
          </filter>
        </defs>
        <g filter={`url(#${filterId})`}>
          {total === 0 ? (
            <circle cx={cx} cy={cy} r={(outer + inner) / 2} fill="none" stroke="#EDEFF4" strokeWidth={outer - inner} />
          ) : parts.length === 1 ? (
            <circle cx={cx} cy={cy} r={(outer + inner) / 2} fill="none" stroke={parts[0].color} strokeWidth={outer - inner}>
              <title>{`${parts[0].label}: ${parts[0].value}`}</title>
            </circle>
          ) : (
            parts.map((part) => (
              <path key={part.label} d={ring(cx, cy, outer, inner, part.start, part.end)} fill={part.color}>
                <title>{`${part.label}: ${part.value}`}</title>
              </path>
            ))
          )}
        </g>
        <text x={cx} y={cy - 2} textAnchor="middle" className="fill-ink" fontSize="22" fontWeight="600">
          {total}
        </text>
        <text x={cx} y={cy + 16} textAnchor="middle" className="fill-ink-subtle" fontSize="11">
          total
        </text>
      </svg>
      <figcaption className="space-y-1.5 min-w-[148px] flex-1">
        <p className="eyebrow mb-2">Share</p>
        {slices.map((slice) => {
          const share = total === 0 ? 0 : Math.round((slice.value / total) * 100);
          return (
            <p key={slice.label} className="flex items-center gap-2 text-meta text-ink">
              <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: slice.color }} />
              <span className="flex-1">{slice.label}</span>
              <span className="tabular font-semibold">{slice.value}</span>
              <span className="tabular text-ink-subtle w-9 text-right">{share}%</span>
            </p>
          );
        })}
      </figcaption>
    </figure>
  );
};

const StageSummary = ({ queue }: { queue: StageQueue }) => {
  const slices = slicesOf(queue.tasks);
  const due = queue.tasks.filter((task) => Boolean(task.dueDate) && !isCompletedStatus(task.status)).length;

  return (
    <section aria-labelledby={`stage-${queue.stage.stageId}`} className="card h-full px-5 py-5 flex flex-col">
      <header className="flex items-start justify-between gap-3 mb-4">
        <h2 id={`stage-${queue.stage.stageId}`} className="text-title font-semibold text-ink">
          {queue.label}
        </h2>
        <p className="shrink-0 text-meta text-ink-subtle">
          <span className="tabular font-semibold text-ink">{due}</span> due
        </p>
      </header>
      <PieChart slices={slices} title={`${queue.label} share`} chartId={queue.stage.stageId} />
    </section>
  );
};

const Dashboard = () => {
  const { currentMember, sessionLoading } = useAuth();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [tasksByStage, setTasksByStage] = useState<Record<string, Task[]>>({});
  const [loading, setLoading] = useState(true);
  const [workflowsLoaded, setWorkflowsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const myStages = useMemo<Stage[]>(() => {
    if (!currentMember?.teamId) return [];
    return workflows
      .flatMap((workflow) => workflow.stages ?? [])
      .filter((stage) => stage.teamId === currentMember.teamId)
      .sort((a, b) => a.stageOrder - b.stageOrder);
  }, [workflows, currentMember]);

  const firstStageIds = useMemo(() => {
    const ids = new Set<string>();
    workflows.forEach((workflow) => {
      const ordered = [...(workflow.stages ?? [])].sort((a, b) => a.stageOrder - b.stageOrder);
      if (ordered[0]) ids.add(ordered[0].stageId);
    });
    return ids;
  }, [workflows]);

  useEffect(() => {
    workflowService
      .getAll()
      .then((data) => setWorkflows(data ?? []))
      .catch(() => setError('Could not load the workflows.'))
      .finally(() => setWorkflowsLoaded(true));
  }, []);

  useEffect(() => {
    if (sessionLoading || !workflowsLoaded) return;
    if (!currentMember || myStages.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const needsRaised = myStages.some((stage) => firstStageIds.has(stage.stageId));
        const raised = needsRaised
          ? ((await taskService.getAllPaginated(undefined, 1, 200)).data ?? []).filter(
              (task) => task.createdByMemberId === currentMember.memberId
            )
          : [];
        const pairs = await Promise.all(
          myStages.map(async (stage) => {
            if (firstStageIds.has(stage.stageId)) return [stage.stageId, raised] as const;
            const atStage = await taskService.getByStage(stage.stageId).catch(() => [] as Task[]);
            return [stage.stageId, atStage.filter((task) => task.assignedToMemberId === currentMember.memberId)] as const;
          })
        );
        if (!cancelled) setTasksByStage(Object.fromEntries(pairs));
      } catch {
        if (!cancelled) setError('Could not load your enquiries.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentMember, myStages, firstStageIds, sessionLoading, workflowsLoaded]);

  if (sessionLoading || loading) return <LoadingSpinner label="Loading your dashboard" />;

  if (!currentMember || !currentMember.teamId) {
    const unlinked = !currentMember;
    return (
      <div>
        <PageHeader title="Dashboard" />
        <div className="card">
          <EmptyState
            icon={unlinked ? <FiUserX /> : <FiUsers />}
            title={unlinked ? "Your login isn't linked to a member" : "You're not on a team yet"}
            body={
              unlinked
                ? 'Work is assigned to members, so nothing can be shown until an administrator links your login to a member record on the Members page.'
                : 'Work reaches you through your team. Ask an administrator to add you to one on the Teams page, and your stages will appear here.'
            }
          />
        </div>
      </div>
    );
  }

  const queues: StageQueue[] = myStages.map((stage) => ({
    stage,
    label: isEnquiryIntake(stage.stageName) ? 'Enquiry' : stage.stageName,
    tasks: tasksByStage[stage.stageId] ?? [],
  }));

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Your enquiries at each stage."
        meta={
          <span className="inline-flex items-center gap-2">
            <Avatar name={`${currentMember.firstName} ${currentMember.lastName}`} size="xs" />
            {currentMember.firstName} {currentMember.lastName}
            {currentMember.teamName ? ` · ${currentMember.teamName}` : ''}
          </span>
        }
      />

      {error && (
        <div role="alert" className="mb-5 flex items-center gap-2.5 px-4 py-3 bg-danger-subtle border border-danger-border rounded-card text-danger text-body">
          <FiAlertCircle aria-hidden="true" className="shrink-0" />
          {error}
        </div>
      )}

      {queues.length === 0 ? (
        <div className="card">
          <EmptyState icon={<FiInbox />} title="No stages for your team" body="Stages appear here once a workflow assigns one to your team." />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {queues.map((queue) => <StageSummary key={queue.stage.stageId} queue={queue} />)}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
